"use client";
import React, { useState } from "react";
import Modal from "./modal";
import { FormData, FormModalProps } from "@/types/modal";
import { useModalContext } from "@/contexts/ModalContext";

const FormModal: React.FC<FormModalProps> = ({
    isOpen,
    onClose,
    title = "Fill the form here",
    submitButtonText = "Download",
    className = "",
    dialogSlug,
}) => {
    const modal = useModalContext();
    const [formData, setFormData] = useState<FormData>({
        name: "",
        companyName: "",
        email: "",
    });

    const [errors, setErrors] = useState<Partial<FormData>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string>("");

    // Reset states when modal opens
    React.useEffect(() => {
        if (isOpen) {
            setSubmitError("");
            setIsSubmitting(false);
        }
    }, [isOpen]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        // Enforce simple client caps
        let nextValue = value;
        if (name === "name") nextValue = value.slice(0, 100);
        if (name === "companyName") nextValue = value.slice(0, 150);
        if (name === "email") nextValue = value.slice(0, 254);

        setFormData(prev => ({
            ...prev,
            [name]: nextValue,
        }));

        // Clear error when user starts typing
        if (errors[name as keyof FormData]) {
            setErrors(prev => ({
                ...prev,
                [name]: "",
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<FormData> = {};

        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
        } else if (formData.name.trim().length < 2) {
            newErrors.name = "Name must be at least 2 characters";
        }

        if (!formData.companyName.trim()) {
            newErrors.companyName = "Company name is required";
        } else if (formData.companyName.trim().length < 2) {
            newErrors.companyName = "Company name must be at least 2 characters";
        }

        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email address";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);
        setSubmitError("");
        
        // Determine slug based on source
        let slugValue = 'unknown';
        
        if (dialogSlug) {
            slugValue = `Dialog - ${dialogSlug}`;
        } else if (title) {
            slugValue = `Dialog - ${title}`;
        } else {
            // Check if this is from a blog page
            const url = typeof window !== "undefined" ? window.location.href : '';
            if (url.includes('/blog/')) {
                const blogMatch = url.match(/\/blog\/([^\/]+)/);
                const blogName = blogMatch ? blogMatch[1] : 'unknown-blog';
                slugValue = `Blog - ${blogName}`;
            } else {
                slugValue = 'Dialog - Unknown';
            }
        }

        const payload = {
            name: formData.name.trim(),
            email: formData.email.trim(),
            message: `Company: ${formData.companyName.trim()}`,
            slug: slugValue,
            sourceUrl: typeof window !== "undefined" ? window.location.href : undefined,
        };

        try {
            const res = await fetch('/api/get-in-touch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await res.json();

            if (!res.ok) {
                // Show server error but do not close the modal
                setSubmitError(data?.error || 'Something went wrong. Please try again.');
                setIsSubmitting(false);
                return;
            }

            // Success: close form modal, reset form, and open thank you modal
            setIsSubmitting(false);
            setFormData({ name: "", companyName: "", email: "" });
            setErrors({});
            setSubmitError("");
            
            // Close form modal and open thank you modal
            onClose();
            modal.openThankYouModal();
        } catch {
            setSubmitError('Network error. Please check your connection and try again.');
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        // Reset form when closing
        setFormData({
            name: "",
            companyName: "",
            email: "",
        });
        setErrors({});
        setSubmitError(""); // Clear error message
        setIsSubmitting(false); // Reset loading state
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={title}
            size="lg"
            className={className}
        >
            <form onSubmit={handleSubmit} className="form-modal-content">
                <div className="form-row">
                    <div className="form-group">
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Your name*"
                            className={`form-control ${errors.name ? "error" : ""}`}
                            maxLength={100}
                            required
                        />
                        {errors.name && <span className="error-message">{errors.name}</span>}
                    </div>

                    <div className="form-group">
                        <input
                            type="text"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleInputChange}
                            placeholder="Company name*"
                            className={`form-control ${errors.companyName ? "error" : ""}`}
                            maxLength={150}
                            required
                        />
                        {errors.companyName && <span className="error-message">{errors.companyName}</span>}
                    </div>
                </div>

                <div className="form-group">
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Your email*"
                        className={`form-control ${errors.email ? "error" : ""}`}
                        maxLength={254}
                        required
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
                {submitError && (
                    <div className="alert alert-danger" role="alert">
                        {submitError}
                    </div>
                )}
                <div className="col-12 justify-content-end d-flex">
                    <button
                        type="submit"
                        className="desilo-link-btn btn-pill mt-2"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm text-white" role="status" aria-hidden="true"></span>
                                Submitting…
                            </>
                        ) : (
                            submitButtonText
                        )}
                        {!isSubmitting && (
                            <span>
                                <i className="fa-solid fa-arrow-right arry1" />
                            </span>
                        )}
                    </button>
                </div>

            </form>
        </Modal>
    );
};

export default FormModal;
